import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight, Sparkles, UserPlus, MapPin, ClipboardList, Handshake, CheckCircle2,
  Camera, Percent, DollarSign, Users, ShieldCheck, Compass, Trophy, Wine, Building2,
  Baby, Waves, Music, Flag, Info, Sliders, Star,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { getLangFromPath, localizePath } from '../../i18n';

const STEP_ACCENTS = [
  { from: 'from-indigo-500', to: 'to-violet-500' },
  { from: 'from-violet-500', to: 'to-fuchsia-500' },
  { from: 'from-fuchsia-500', to: 'to-pink-500' },
];
const STEP_ICONS = [ClipboardList, UserPlus, Handshake];
const TYPE_ICONS = [Trophy, Wine, Building2, Music, Waves, Baby, Camera, Flag];

/* --- 3-step process card --- */
function StepCard({ n, Icon, title, desc, accent }) {
  return (
    <div className="relative rounded-3xl bg-[#111116] border border-white/8 p-7 flex flex-col h-full">
      <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#0a0a0f] border border-white/10 flex items-center justify-center text-[11px] font-bold text-white/70">{n}</div>
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${accent.from} ${accent.to} flex items-center justify-center text-white shadow-lg mb-5`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display text-xl font-semibold text-white mb-2">{title}</div>
      <div className="text-sm text-white/65 leading-relaxed flex-1">{desc}</div>
    </div>
  );
}

function useSupply(t) {
  return (level, kind) => {
    const key = level < 40 || level < 80 ? '' : ''; // dummy so eslint keeps t used
    return t(`services.photographersOnDemand.pricing.${kind}.${level}`);
  };
}

function PercentModel() {
  const { t } = useTranslation();
  const [pct, setPct] = useState(70);
  const supplyKey = pct < 40 ? 'low' : pct < 60 ? 'medium' : pct < 80 ? 'high' : 'veryHigh';
  const descKey = { low: 'lowDesc', medium: 'mediumDesc', high: 'highDesc', veryHigh: 'veryHighDesc' }[supplyKey];
  const supplyColor = pct < 40 ? 'text-rose-300' : pct < 60 ? 'text-amber-300' : pct < 80 ? 'text-emerald-300' : 'text-cyan-300';
  const supplyBg = pct < 40 ? 'bg-rose-500/10 border-rose-400/25' : pct < 60 ? 'bg-amber-500/10 border-amber-400/25' : pct < 80 ? 'bg-emerald-500/10 border-emerald-400/25' : 'bg-cyan-500/10 border-cyan-400/25';

  return (
    <div className="rounded-3xl bg-[#111116] border border-white/8 p-7 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white shadow-lg">
          <Percent className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold">{t('services.photographersOnDemand.pricing.percentModel.label')}</div>
          <div className="font-display text-xl font-bold text-white">{t('services.photographersOnDemand.pricing.percentModel.title')}</div>
        </div>
      </div>
      <p className="text-sm text-white/65 leading-relaxed mb-6">
        {t('services.photographersOnDemand.pricing.percentModel.description')}
      </p>

      <div className="rounded-2xl bg-[#0a0a10] border border-white/8 p-5 mb-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">{t('services.photographersOnDemand.pricing.percentModel.sliderLabel')}</div>
          <div className="font-display text-3xl font-bold text-white">{pct}%</div>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={pct}
          onChange={(e) => setPct(parseInt(e.target.value, 10))}
          data-testid="percent-slider"
          className="w-full h-1.5 rounded-full appearance-none bg-white/10 accent-indigo-400 cursor-pointer"
          style={{ background: `linear-gradient(to right, rgb(129 140 248) 0%, rgb(167 139 250) ${pct}%, rgba(255,255,255,0.08) ${pct}%, rgba(255,255,255,0.08) 100%)` }}
        />
        <div className="flex justify-between mt-2 text-[10px] text-white/40">
          <span>{t('services.photographersOnDemand.pricing.percentModel.minLabel')}</span>
          <span>{t('services.photographersOnDemand.pricing.percentModel.midLabel')}</span>
          <span>{t('services.photographersOnDemand.pricing.percentModel.maxLabel')}</span>
        </div>
      </div>

      <div className={`rounded-xl border px-4 py-3 ${supplyBg} flex items-center justify-between gap-3`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{t('services.photographersOnDemand.pricing.percentModel.availabilityLabel')}</div>
          <div className={`text-sm font-bold ${supplyColor}`}>{t(`services.photographersOnDemand.pricing.supplyLevels.${supplyKey}`)} · {t(`services.photographersOnDemand.pricing.supplyDescriptions.${descKey}`)}</div>
        </div>
        <Sparkles className={`w-5 h-5 ${supplyColor}`} />
      </div>
    </div>
  );
}

function FixedFeeModel() {
  const { t } = useTranslation();
  const [fee, setFee] = useState(150);
  const supplyKey = fee < 80 ? 'low' : fee < 150 ? 'medium' : fee < 250 ? 'high' : 'veryHigh';
  const descKey = { low: 'lowDesc', medium: 'mediumDesc', high: 'highDesc', veryHigh: 'veryHighDesc' }[supplyKey];
  const supplyColor = fee < 80 ? 'text-rose-300' : fee < 150 ? 'text-amber-300' : fee < 250 ? 'text-emerald-300' : 'text-cyan-300';
  const supplyBg = fee < 80 ? 'bg-rose-500/10 border-rose-400/25' : fee < 150 ? 'bg-amber-500/10 border-amber-400/25' : fee < 250 ? 'bg-emerald-500/10 border-emerald-400/25' : 'bg-cyan-500/10 border-cyan-400/25';

  return (
    <div className="rounded-3xl bg-[#111116] border border-white/8 p-7 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
          <DollarSign className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">{t('services.photographersOnDemand.pricing.feeModel.label')}</div>
          <div className="font-display text-xl font-bold text-white">{t('services.photographersOnDemand.pricing.feeModel.title')}</div>
        </div>
      </div>
      <p className="text-sm text-white/65 leading-relaxed mb-6">
        {t('services.photographersOnDemand.pricing.feeModel.description')}
      </p>

      <div className="rounded-2xl bg-[#0a0a10] border border-white/8 p-5 mb-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">{t('services.photographersOnDemand.pricing.feeModel.sliderLabel')}</div>
          <div className="font-display text-3xl font-bold text-white">${fee} USD</div>
        </div>
        <input
          type="range"
          min="0"
          max="400"
          step="10"
          value={fee}
          onChange={(e) => setFee(parseInt(e.target.value, 10))}
          data-testid="fee-slider"
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
          style={{ background: `linear-gradient(to right, rgb(251 191 36) 0%, rgb(249 115 22) ${(fee / 400) * 100}%, rgba(255,255,255,0.08) ${(fee / 400) * 100}%, rgba(255,255,255,0.08) 100%)` }}
        />
        <div className="flex justify-between mt-2 text-[10px] text-white/40">
          <span>{t('services.photographersOnDemand.pricing.feeModel.minLabel')}</span>
          <span>{t('services.photographersOnDemand.pricing.feeModel.midLabel')}</span>
          <span>{t('services.photographersOnDemand.pricing.feeModel.maxLabel')}</span>
        </div>
      </div>

      <div className={`rounded-xl border px-4 py-3 ${supplyBg} flex items-center justify-between gap-3`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{t('services.photographersOnDemand.pricing.percentModel.availabilityLabel')}</div>
          <div className={`text-sm font-bold ${supplyColor}`}>{t(`services.photographersOnDemand.pricing.supplyLevels.${supplyKey}`)} · {t(`services.photographersOnDemand.pricing.supplyDescriptions.${descKey}`)}</div>
        </div>
        <Sparkles className={`w-5 h-5 ${supplyColor}`} />
      </div>
    </div>
  );
}

function LatamCoverage() {
  const { t } = useTranslation();
  const cities = [
    { name: 'CDMX', country: 'MX', photographers: '420+', top: '15%', left: '15%' },
    { name: 'Bogotá', country: 'CO', photographers: '180+', top: '32%', left: '22%' },
    { name: 'Lima', country: 'PE', photographers: '140+', top: '48%', left: '20%' },
    { name: 'Santiago', country: 'CL', photographers: '220+', top: '72%', left: '25%' },
    { name: 'BsAs', country: 'AR', photographers: '380+', top: '78%', left: '42%' },
    { name: 'São Paulo', country: 'BR', photographers: '460+', top: '58%', left: '52%' },
    { name: 'Rio', country: 'BR', photographers: '210+', top: '58%', left: '62%' },
    { name: 'Montevideo', country: 'UY', photographers: '90+', top: '76%', left: '48%' },
    { name: 'Punta Cana', country: 'DO', photographers: '110+', top: '10%', left: '55%' },
    { name: 'Miami', country: 'US', photographers: '270+', top: '2%', left: '30%' },
  ];
  const photographersLabel = t('services.photographersOnDemand.coverage.photographers');

  return (
    <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0b0b12] via-[#111116] to-[#0b0b12] aspect-[4/5] md:aspect-[5/6] p-6">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
      <div className="absolute top-1/4 -right-20 w-[300px] h-[300px] bg-indigo-500/20 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -left-20 w-[300px] h-[300px] bg-fuchsia-500/15 rounded-full blur-[100px]" />

      <div className="relative flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-semibold flex items-center gap-1.5">
            <MapPin className="w-3 h-3" /> {t('services.photographersOnDemand.coverage.network')}
          </div>
          <div className="font-display text-xl font-bold text-white mt-1">{t('services.photographersOnDemand.coverage.title')}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold text-white leading-none">2.480+</div>
          <div className="text-[10px] text-white/50 uppercase tracking-wider mt-0.5">{t('services.photographersOnDemand.coverage.active')}</div>
        </div>
      </div>

      <div className="relative w-full h-[85%]">
        {cities.map((c, i) => (
          <div key={c.name} className="absolute flex items-center gap-2" style={{ top: c.top, left: c.left }}>
            <div className="relative">
              <div className="w-3 h-3 rounded-full bg-indigo-400 shadow-lg shadow-indigo-500/50" />
              <div className="absolute inset-0 w-3 h-3 rounded-full bg-indigo-400 animate-ping opacity-40" style={{ animationDelay: `${i * 150}ms` }} />
            </div>
            <div className="rounded-md bg-[#0a0a10]/90 backdrop-blur border border-white/10 px-2 py-1">
              <div className="text-[10px] font-bold text-white leading-tight">{c.name} <span className="text-white/40">{c.country}</span></div>
              <div className="text-[9px] text-indigo-300 leading-tight">{c.photographers} {photographersLabel}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PhotographersOnDemand() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const steps = t('services.photographersOnDemand.process.steps', { returnObjects: true }) || [];
  const types = t('services.photographersOnDemand.types.items', { returnObjects: true }) || [];
  const checklist = t('services.photographersOnDemand.management.checklist', { returnObjects: true }) || [];

  const shortlistItems = [
    { name: 'Julián R.', city: 'Buenos Aires · AR', specialtyKey: 'sports', rating: 4.9, jobs: 148 },
    { name: 'Sofía M.', city: 'Ciudad de México · MX', specialtyKey: 'corporate', rating: 4.8, jobs: 92 },
    { name: 'Rafael L.', city: 'São Paulo · BR', specialtyKey: 'festivals', rating: 5.0, jobs: 74 },
    { name: 'Camila P.', city: 'Santiago · CL', specialtyKey: 'hospitality', rating: 4.9, jobs: 61 },
  ];

  return (
    <>
      <SEO
        title={t('services.photographersOnDemand.seoTitle')}
        description={t('services.photographersOnDemand.seoDescription')}
        path="/servicios/fotografos-on-demand"
        schema={serviceSchema({ name: 'Red de Fotógrafos on Demand LATAM', description: 'Servicio de fotógrafos profesionales seleccionados y coordinados por momentIA en toda Latinoamérica.', path: '/servicios/fotografos-on-demand', area: 'LATAM' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-indigo-500/15 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-fuchsia-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-indigo-300 mb-5 hover:text-indigo-200 transition-colors">
                <span>{t('services.photographersOnDemand.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.photographersOnDemand.breadcrumbCurrent')}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                {t('services.photographersOnDemand.heroTitle1')}
                <span className="block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  {t('services.photographersOnDemand.heroTitleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('services.photographersOnDemand.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={to('/request-demo')}>
                  <Button data-testid="ondemand-request-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('services.photographersOnDemand.ctaRequest')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/vende-tus-fotos')}>
                  <Button data-testid="ondemand-join-btn" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.photographersOnDemand.ctaJoin')}
                  </Button>
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <div className="font-display text-2xl font-bold text-white">2.480+</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.photographersOnDemand.stats.network')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">18</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.photographersOnDemand.stats.countries')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">72h</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.photographersOnDemand.stats.selectionTime')}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <LatamCoverage />
            </div>
          </div>
        </div>
      </section>

      {/* 3-STEP PROCESS */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-3">{t('services.photographersOnDemand.process.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.photographersOnDemand.process.titleLine1')}
              <br />
              <span className="bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.photographersOnDemand.process.titleHighlight')}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {steps.map((s, i) => (
              <StepCard key={s.n} n={s.n} Icon={STEP_ICONS[i]} title={s.title} desc={s.desc} accent={STEP_ACCENTS[i]} />
            ))}
          </div>
        </div>
      </section>

      {/* PRICING MODELS */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-3 flex items-center gap-2">
              <Sliders className="w-3.5 h-3.5" /> {t('services.photographersOnDemand.pricing.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.photographersOnDemand.pricing.titleLine1')}
              <br />
              <span className="bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.photographersOnDemand.pricing.titleHighlight')}</span>
            </h2>
            <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl">
              {t('services.photographersOnDemand.pricing.description')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <PercentModel />
            <FixedFeeModel />
          </div>

          <div className="mt-8 max-w-4xl mx-auto rounded-2xl border border-white/10 bg-[#0e0e13] px-5 md:px-7 py-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/12 border border-amber-400/25 flex items-center justify-center text-amber-300 shrink-0">
              <Info className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">{t('services.photographersOnDemand.pricing.disclosure.title')}</div>
              <div className="text-sm text-white/60 mt-1 leading-relaxed">
                {t('services.photographersOnDemand.pricing.disclosure.desc')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTOGRAPHER TYPES */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-3">{t('services.photographersOnDemand.types.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.photographersOnDemand.types.titleLine1')}
              <br />
              {t('services.photographersOnDemand.types.titleLine2')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {types.map((item, i) => {
              const Icon = TYPE_ICONS[i] || Camera;
              return (
                <div key={item.name} className="card-lift rounded-2xl bg-[#111116] border border-white/8 p-5">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-400/25 flex items-center justify-center text-indigo-300 mb-4">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="font-display text-base font-semibold text-white leading-tight">{item.name}</div>
                  <div className="text-xs text-white/60 mt-1 leading-relaxed">{item.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY MOMENTIA MANAGES */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> {t('services.photographersOnDemand.management.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.photographersOnDemand.management.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-indigo-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.photographersOnDemand.management.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.photographersOnDemand.management.description')}
              </p>
              <ul className="mt-8 space-y-3">
                {checklist.map((text) => (
                  <li key={text} className="flex items-start gap-3 text-white/85">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm leading-relaxed">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-[#0d0d13] border border-white/8 p-6 md:p-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 mb-4">{t('services.photographersOnDemand.shortlist.eyebrow')}</div>
              <div className="space-y-3">
                {shortlistItems.map((p) => (
                  <div key={p.name} className="flex items-center gap-3 rounded-xl bg-[#111116] border border-white/8 p-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white font-display font-bold text-sm shrink-0">
                      {p.name.split(' ').map((s) => s[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-white truncate">{p.name}</div>
                        <div className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-emerald-300 font-semibold">{t('services.photographersOnDemand.shortlist.verified')}</div>
                      </div>
                      <div className="text-[11px] text-white/50 mt-0.5">{p.city} · {t(`services.photographersOnDemand.shortlist.specialties.${p.specialtyKey}`)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm text-white font-semibold flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> {p.rating}
                      </div>
                      <div className="text-[10px] text-white/40">{p.jobs} {t('services.photographersOnDemand.shortlist.jobs')}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/8 text-center">
                <div className="text-[10px] uppercase tracking-wider text-white/40">{t('services.photographersOnDemand.shortlist.typicalLabel')}</div>
                <div className="font-display text-lg font-bold text-white">{t('services.photographersOnDemand.shortlist.typicalValue')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-indigo-400/25 bg-gradient-to-br from-[#141425] via-[#151020] to-[#1e1425] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-[100px]" />
            <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-fuchsia-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-indigo-400/25 bg-indigo-500/10 text-xs text-indigo-200 mb-6">
                <Compass className="w-3 h-3" /> {t('services.photographersOnDemand.finalCta.badge')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white">
                {t('services.photographersOnDemand.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.photographersOnDemand.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button data-testid="ondemand-cta-btn" className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.photographersOnDemand.finalCta.ctaRequest')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/vende-tus-fotos')}>
                  <Button data-testid="ondemand-cta-join-btn" variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    <Users className="mr-2 w-4 h-4" /> {t('services.photographersOnDemand.finalCta.ctaJoin')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
